import type { ComponentChildren } from 'preact';
import { useMemo } from 'preact/hooks';
import { usePageMeta } from '../lib/meta';
import { Wordmark } from '../components/Wordmark';
import {
  chromeFor,
  contentFor,
  formatUpdated,
  legal,
  parseInline,
  pickLocale,
  type LegalBlock,
} from '../lib/legal';

/** Header / footer / title chrome shared by the JSON-driven legal pages, the
 *  interactive Delete-account page and the /link invite page. [updated] is a
 *  preformatted string; omit it on pages that aren't versioned documents. */
export function LegalShell({
  title,
  updated,
  description,
  children,
}: {
  title: string;
  updated?: string;
  /** Meta description for search results. */
  description?: string;
  children: ComponentChildren;
}) {
  usePageMeta(`${title} — shotmeal`, description);
  const locale = useMemo(pickLocale, []);
  const chrome = chromeFor(locale);

  return (
    <main class="page-in" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header style={{ padding: '24px 0', borderBottom: '1px solid var(--line-soft)' }}>
        <div class="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <a href="/" style={{ textDecoration: 'none', display: 'inline-flex' }}>
            <Wordmark size={24} />
          </a>
          <a href="/" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', textDecoration: 'none' }}>
            ← {chrome.home}
          </a>
        </div>
      </header>
      <div class="container" style={{ flex: 1, padding: '40px 24px 64px', maxWidth: 720 }}>
        {updated && (
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              color: 'var(--ink-3)',
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            {chrome.lastUpdated} {updated}
          </div>
        )}
        <h1
          class="wordmark"
          style={{
            margin: '8px 0 24px',
            fontSize: 'clamp(36px, 5vw, 56px)',
            letterSpacing: '-0.02em',
            color: 'var(--ink)',
          }}
        >
          {title}
        </h1>
        <div style={{ color: 'var(--ink-2)', fontSize: 16, lineHeight: 1.65 }}>
          {children}
        </div>
      </div>
      <footer style={{ padding: '32px 0', borderTop: '1px solid var(--line-soft)' }}>
        <div class="container" style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {[
            [chrome.nav.privacy, '/privacy'],
            [chrome.nav.terms, '/terms'],
            [chrome.nav.support, '/support'],
            [chrome.nav.deleteAccount, '/delete-account'],
          ].map(([label, href]) => (
            <a
              key={href}
              href={href}
              style={{ fontSize: 13, color: 'var(--ink-2)', textDecoration: 'none' }}
            >
              {label}
            </a>
          ))}
        </div>
      </footer>
    </main>
  );
}

/** Renders one shared-JSON document (privacy / terms / support) in the
 *  visitor's browser language, falling back to English. Kept in sync with the
 *  Flutter in-app sheet — both read the same source. */
// Search-result snippets for the legal pages (English — the site's language).
const DESCRIPTIONS: Record<string, string> = {
  privacy:
    'How shotmeal handles your data: meals stay on your phone unless you turn on backup, your weight is never shared, and we never sell data or train on your meals.',
  terms: 'The terms for using shotmeal and shotmeal Pro.',
  support:
    'Help with shotmeal: accounts, backup, subscriptions, Apple Health and Health Connect, and how to reach us.',
};

export function LegalPage({ docKey }: { docKey: string }) {
  const locale = useMemo(pickLocale, []);
  const chrome = chromeFor(locale);
  const doc = legal.documents[docKey];
  const content = contentFor(doc, locale);

  return (
    <LegalShell
      title={content.title}
      updated={formatUpdated(doc.updated, locale)}
      description={DESCRIPTIONS[docKey]}
    >
      {content.body.map((block, i) => (
        <Block key={i} block={block} />
      ))}
      {locale !== 'en' && (
        <p style={{ marginTop: 32, fontSize: 13, fontStyle: 'italic', color: 'var(--ink-3)' }}>
          {chrome.englishGoverns}
        </p>
      )}
    </LegalShell>
  );
}

/** Inline markup (`**bold**`, `[label](url)`) → VNodes. */
function Inline({ text }: { text: string }): ComponentChildren {
  return parseInline(text).map((seg, i) => {
    if (seg.kind === 'bold') return <strong key={i}>{seg.text}</strong>;
    if (seg.kind === 'link') {
      return (
        <a key={i} href={seg.url} style={{ color: 'var(--ember)', fontWeight: 600 }}>
          {seg.text}
        </a>
      );
    }
    return <span key={i}>{seg.text}</span>;
  });
}

function Block({ block }: { block: LegalBlock }) {
  switch (block.type) {
    case 'h3':
      return <h3><Inline text={block.text} /></h3>;
    case 'h4':
      return <h4><Inline text={block.text} /></h4>;
    case 'ul':
      return (
        <ul>
          {block.items.map((item, i) => (
            <li key={i}><Inline text={item} /></li>
          ))}
        </ul>
      );
    default:
      return <p><Inline text={block.text} /></p>;
  }
}
